import { useEffect, useState } from 'react';
import type { PhaseCoverage } from '@4cm/core/phaseOfPlay';
import { supabase } from '../supabase';
import { loadLocalSessions } from '../localTeam';
import type { LoadState } from '../loadState';

export interface NowSession {
  id: string;
  date: string;
  title: string | null;
  duration_mins: number | null;
  objectives: string[] | null;
  psych_social_intent: string | null;
  phase_coverage: Partial<PhaseCoverage> | null;
}

export type NowSessionSource = 'live' | 'local';

interface NowSessionResult {
  session: NowSession | null;
  state: LoadState;
  source: NowSessionSource;
  errorMessage?: string;
  /** @deprecated alias kept while consumers migrate to `state`. */
  loading: boolean;
}

/**
 * Next upcoming session for a team — falls back to the most recent past
 * session if nothing scheduled, then to localStorage if Supabase is
 * unreachable. Returns the shared LoadState so Home can render
 * empty vs error distinctly.
 */
export function useNowSession(teamId: string | undefined): NowSessionResult {
  const [result, setResult] = useState<NowSessionResult>({
    session: null,
    state: 'loading',
    source: 'live',
    loading: true,
  });

  useEffect(() => {
    if (!teamId) {
      setResult({ session: null, state: 'empty', source: 'live', loading: false });
      return;
    }
    let cancelled = false;
    setResult({ session: null, state: 'loading', source: 'live', loading: true });

    (async () => {
      let liveError: string | undefined;
      try {
        const now = new Date().toISOString();
        const { data: upcoming, error: upcomingError } = await supabase
          .from('sessions')
          .select('id, date, title, duration_mins, objectives, psych_social_intent, phase_coverage')
          .eq('team_id', teamId)
          .gte('date', now)
          .order('date', { ascending: true })
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        if (upcomingError) {
          liveError = upcomingError.message;
        } else if (upcoming) {
          setResult({ session: upcoming as NowSession, state: 'ok', source: 'live', loading: false });
          return;
        } else {
          const { data: latest, error: latestError } = await supabase
            .from('sessions')
            .select('id, date, title, duration_mins, objectives, psych_social_intent, phase_coverage')
            .eq('team_id', teamId)
            .order('date', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (cancelled) return;
          if (latestError) {
            liveError = latestError.message;
          } else if (latest) {
            setResult({ session: latest as NowSession, state: 'ok', source: 'live', loading: false });
            return;
          }
        }
      } catch (err) {
        if (cancelled) return;
        liveError = err instanceof Error ? err.message : 'fetch failed';
      }

      // Fallback: localStorage from Onboarding
      const local = loadLocalSessions().filter((s) => s.team_id === teamId);
      const todayMs = Date.now();
      const upcoming = local.filter((s) => new Date(s.date).getTime() >= todayMs).sort((a, b) => +new Date(a.date) - +new Date(b.date))[0];
      const fallback = upcoming ?? local.sort((a, b) => +new Date(b.date) - +new Date(a.date))[0];
      if (fallback) {
        setResult({
          session: {
            id: fallback.id,
            date: fallback.date,
            title: fallback.title,
            duration_mins: fallback.duration_mins,
            objectives: fallback.objectives,
            psych_social_intent: fallback.psych_social_intent,
            phase_coverage: fallback.phase_coverage as Partial<PhaseCoverage>,
          },
          state: 'ok',
          source: 'local',
          loading: false,
        });
        return;
      }
      setResult(
        liveError
          ? { session: null, state: 'error', source: 'live', errorMessage: liveError, loading: false }
          : { session: null, state: 'empty', source: 'live', loading: false },
      );
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  return result;
}
